// Start and Exit for "Act as client" (plan v4 §6 Start / Exit). Both end in a hard navigation, which resets
// module state, the SSE hub (restarted with the new tag), OwnerScope and identityGen in one step.
import type { QueryClient } from '@tanstack/react-query';
import { ApiError, del, post } from './api';
import { clearPendingMemory } from './pending';
import { idbDel, idbKeys } from './idb';
import { getTheme, setTheme, type ThemeMode } from './theme';
import { abortAll, isLive, uploadsSnapshot } from '../uploads/store';
import { ACTING_MARKER, RETURN_KEY, THEME_KEY_SAVED, broadcastIdentity, bumpIdentityGen, firstName, getSessionTag } from './impersonation';

const ss = () => {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
};
const ls = () => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

/** Navigation seam (tests replace it). */
export const nav = { assign: (url: string) => window.location.assign(url) };

/** Uploads still running in this tab; Start and Exit confirm before cancelling them. */
export const liveUploadCount = () => uploadsSnapshot().filter((u) => isLive(u.state)).length;

/** Readable copy for the §4 error codes (§6 Entry points). */
export function impersonationErrorText(e: unknown, clientName: string): string {
  const name = firstName(clientName) || 'This client';
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'impersonation_out_of_scope':
        return `${name} also uses a business you aren't assigned to.`;
      case 'impersonation_disabled':
        return 'Acting as clients is turned off in settings.';
      case 'already_impersonating':
        return "You're already acting as a client. Exit first.";
      case 'impersonation_forbidden':
        return "You can't act as this person.";
    }
    if (e.status === 404) return "This client can't be opened right now.";
    if (e.status === 429) return 'Too many tries. Wait a few minutes and try again.';
    if (e.status === 0 || e.status >= 500) return e.message;
    return e.message || "This client can't be opened right now.";
  }
  return "Couldn't start. Check your connection and try again.";
}

/**
 * Start acting as `userId`. Throws (with the server error) before anything local changes if the server
 * refuses, so the caller can show impersonationErrorText(). On success it hard-navigates to the client home.
 */
export async function startImpersonation(qc: QueryClient, userId: string): Promise<void> {
  const theme = getTheme();
  const back = window.location.pathname + window.location.search;
  await abortAll();
  await post('/api/auth/impersonate', { userId });
  ss()?.setItem(THEME_KEY_SAVED, theme);
  ss()?.setItem(RETURN_KEY, back);
  ls()?.setItem(ACTING_MARKER, '1');
  bumpIdentityGen();
  broadcastIdentity();
  clearPendingMemory();
  qc.clear();
  nav.assign('/');
}

/** Exit (also used from the frozen dialog). Never throws for a lost session: that ends at /login. */
export async function exitImpersonation(qc: QueryClient, clientId: string): Promise<void> {
  // 1–2. Best-effort cancel of this session's unfinished uploads (the server cancels them too), then abort.
  const live = uploadsSnapshot().filter((u) => isLive(u.state) && u.reservationId);
  await Promise.all(live.map((u) => del(`/api/upload-reservations/${u.reservationId}`).catch(() => undefined)));
  await abortAll();
  // 3. End: restores the staff session from the return cookie when it is still valid.
  let restored = false;
  try {
    const r = await post<{ restored?: boolean }>('/api/auth/impersonate/end', { sessionTag: getSessionTag() ?? undefined });
    restored = !!r?.restored;
  } catch (e) {
    // Offline: stay put so the person can try again. Any server answer means this session is over.
    if (!(e instanceof ApiError) || e.status === 0) throw e;
    if (e.code === 'session_changed') return; // another tab switched: api.ts already reloads this one
  }
  // 4. Drafts the client composer kept on this device for the client (keys `${clientId}:…`).
  try {
    const keys = await idbKeys(`${clientId}:`);
    await Promise.all(keys.map((k) => idbDel(k)));
  } catch {
    /* best effort */
  }
  // 5–6.
  clearPendingMemory();
  const saved = ss()?.getItem(THEME_KEY_SAVED) as ThemeMode | null;
  if (saved) setTheme(saved); // local only, never PATCHed
  const back = ss()?.getItem(RETURN_KEY);
  ss()?.removeItem(THEME_KEY_SAVED);
  ss()?.removeItem(RETURN_KEY);
  ls()?.removeItem(ACTING_MARKER);
  // 7–8.
  bumpIdentityGen();
  broadcastIdentity();
  qc.clear();
  nav.assign(restored ? (back && back.startsWith('/') && !back.startsWith('//') ? back : '/staff') : '/login');
}
